import {
  BillingPlanDto,
  BillingSubscriptionDto,
  REMINDERS_PAUSED_REASONS,
  resolveRemindersPausedReason,
} from '@shared/core/billing'
import { USER_EVENTS } from '@shared/core/user-events'
import { GetProfileResponseDto } from '@shared/core/users'
import { ms } from '@shared/util/ms'
import classNames from 'classnames'
import { format } from 'date-fns'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_PAUSED_REMINDERS_ALERT_STATE, PausedRemindersAlertState } from 'src/modules/alerts/alerts.types'
import { FormattedText, Typography } from 'src/modules/core/components'
import { Icon } from 'src/modules/core/components/icon/icon'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useUpdatePaymentMethod } from 'src/modules/payment/hooks/use-update-payment-method'
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'
import { formatSubscriptionCancellationDisplay, parseBillingPausedAt } from 'src/modules/payment/utils/helpers'
import { useHasUserEvent } from 'src/modules/user-events/use-has-user-event'
import styles from './account-snapshot.module.scss'
import { getDaysRemaining, getNextResetDate } from './account-snapshot.utils'
import { MetricCard } from './metric-card'
import { MissingRemindersPanel } from './missing-reminders-panel'
import { PausedPlanCard } from './paused-plan-card'
import type { PaymentPausedPlanCardProps } from './paused-plan-card/paused-plan-card.interface'

interface AccountSnapshotProps {
  profile?: GetProfileResponseDto
  subscription?: BillingSubscriptionDto
  currentPlan?: BillingPlanDto
  onUpgradeClick?: () => void
  pausedReminders?: PausedRemindersAlertState
  onResumeRemindersClick: () => void
}

/**
 * Limit copy for accounts whose billing UI is hidden. Factual only: this cohort
 * has nothing to upgrade to, so an upgrade call to action would be a dead end.
 */
function getHiddenBillingLimitError(limitReached: boolean, overLimit: boolean, limit?: number): React.ReactNode | null {
  if (!limitReached && !overLimit) {
    return null
  }

  const id = limitReached ? 'payment.error.freeLimitReached' : 'payment.error.freeOverLimit'

  return (
    <FormattedText
      id={id}
      values={{
        strong: (chunks) => <strong>{chunks}</strong>,
        limit,
      }}
    />
  )
}

function getError(
  profile?: GetProfileResponseDto,
  subscription?: BillingSubscriptionDto,
  currentPlan?: BillingPlanDto,
  hasGoogleApiQuotaExceeded?: boolean,
  hideBillingUi?: boolean,
): React.ReactNode | null {
  // Free-trial accounts read the server's paused flag: the sent counter can overshoot the limit.
  const limitReached =
    profile?.billing_subscription_id || hideBillingUi
      ? profile?.reminders_sent_during_billing_cycle === currentPlan?.reminders_limit
      : Boolean(profile?.reminders_paused)

  const overLimit =
    profile?.reminders_sent_during_billing_cycle + profile?.include?.count_scheduled_reminders >
    currentPlan?.reminders_limit

  if (hasGoogleApiQuotaExceeded) {
    return (
      <div>
        <strong>We can&apos;t refresh your events right now.</strong> Your Google account reached a temporary request
        limit when syncing your calendars. This will resolve shortly. Reload the page in a few minutes.
      </div>
    )
  }

  // Past due cannot apply to an account with no paid subscription, and the
  // banner points at a billing screen this cohort cannot open.
  if (hideBillingUi) {
    return getHiddenBillingLimitError(limitReached, overLimit, currentPlan?.reminders_limit)
  }

  if (subscription?.state === SubscriptionStatus.past_due) {
    return (
      <FormattedText
        id="payment.error.status"
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
          nextBillDate: subscription?.next_payment?.date,
        }}
      />
    )
  }

  // The paused plan card carries this message, and the limit copy below
  // promises a cycle renewal the free allowance never gets.
  if (profile?.reminders_paused) {
    return null
  }

  if (limitReached) {
    return <FormattedText id="payment.error.limitReached" values={{ strong: (chunks) => <strong>{chunks}</strong> }} />
  }

  if (overLimit) {
    return <FormattedText id="payment.error.overLimit" values={{ strong: (chunks) => <strong>{chunks}</strong> }} />
  }

  return null
}

function getCancellationDisplay(profile?: GetProfileResponseDto): { status: string; date: string } | null {
  if (!profile) {
    return null
  }

  const cancellationDate = formatSubscriptionCancellationDisplay(profile)
  if (!cancellationDate) {
    return null
  }

  const today = new Date()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const status = cancellationDate < todayStart ? 'Subscription ended' : 'Subscription ends'

  return {
    status,
    date: format(cancellationDate, 'MMMM d, yyyy'),
  }
}

export function AccountSnapshot({
  profile,
  subscription,
  currentPlan,
  pausedReminders = DEFAULT_PAUSED_REMINDERS_ALERT_STATE,
  onResumeRemindersClick,
}: AccountSnapshotProps) {
  const navigate = useNavigate()
  const { data: hasGoogleApiQuotaExceeded } = useHasUserEvent(
    USER_EVENTS.GOOGLE_API_QUOTA_EXCEEDED,
    new Date(Date.now() - ms('5m')),
  )

  const hideBillingUi = Boolean(profile?.hide_billing_ui)
  const planType = currentPlan?.name || 'Free Trial'
  const remindersCount = profile?.reminders_sent_during_billing_cycle || 0
  const scheduledReminders = profile?.scheduled_reminders_count || 0
  const remainingReminders = currentPlan?.reminders_limit - remindersCount
  const now = new Date()
  const nextResetDate = getNextResetDate(profile, now)
  const daysRemaining = getDaysRemaining(nextResetDate, now)
  const error = getError(profile, subscription, currentPlan, hasGoogleApiQuotaExceeded, hideBillingUi)
  const cancellationDisplay = getCancellationDisplay(profile)
  const pausedReason = profile ? resolveRemindersPausedReason(profile) : null

  const handleChangeSubscription = useCallback(() => {
    navigate(`${routeNames.settings.path}?tab=plan`)
  }, [navigate])

  const errorBanner = error && (
    <div className={styles.error}>
      <span className={styles.errorIcon}>⚠</span>
      {error}
    </div>
  )

  if (pausedReason) {
    const isPaymentPaused = pausedReason === REMINDERS_PAUSED_REASONS.paymentPaused
    const PlanCard = isPaymentPaused ? PaymentPausedPlanCard : PausedPlanCard
    const pausedAt = isPaymentPaused ? parseBillingPausedAt(profile) : null
    const pausedOn = pausedAt ? format(pausedAt, 'M/d/yyyy') : undefined

    return (
      <>
        <div className={classNames(styles.container, styles.pausedContainer)}>
          <PlanCard
            reason={pausedReason}
            sentCount={remindersCount}
            limit={profile.effective_free_reminders_limit}
            date={isPaymentPaused ? pausedOn : cancellationDisplay?.date}
            planName={currentPlan?.name}
            onResumeClick={onResumeRemindersClick}
          />
          <MissingRemindersPanel reason={pausedReason} pausedReminders={pausedReminders} />
        </div>
        {errorBanner}
      </>
    )
  }

  return (
    <>
      <div className={styles.container}>
        {/* This cohort has no plan to name, no renewal to announce, and nothing
            to upgrade to, so the whole title section is dropped. */}
        {hideBillingUi ? null : (
          <div className={styles.titleSection}>
            <Typography variant="h2" className={styles.title}>
              {planType}
            </Typography>
            {cancellationDisplay ? (
              <div className={styles.cancellationInfo}>
                <Typography variant="span" className={styles.cancellationStatus}>
                  {cancellationDisplay.status}
                </Typography>
                <Typography variant="span" className={styles.cancellationDate}>
                  {cancellationDisplay.date}
                </Typography>
              </div>
            ) : (
              <button className={styles.upgradeButton} onClick={handleChangeSubscription}>
                <Icon name="arrowLeft" className={styles.collapseIcon} />
                Upgrade
              </button>
            )}
          </div>
        )}

        <div className={styles.metricsContainer}>
          <MetricCard
            value={remindersCount}
            label={hideBillingUi ? 'Reminders sent this month' : 'Reminders sent this billing cycle'}
            icon="bell"
          />
          <MetricCard value={scheduledReminders} label="Reminders scheduled" icon="bell-calendar" />
          <MetricCard value={remainingReminders} label="Reminders remaining" icon="bell-dotted" />
          {!hideBillingUi && daysRemaining !== 0 ? (
            <MetricCard value={daysRemaining} label="Days remaining this billing cycle" icon="calendar" />
          ) : null}
        </div>
      </div>
      {errorBanner}
    </>
  )
}

/** Mounted for a payment pause only, so no other account loads the card-update flow. */
function PaymentPausedPlanCard(props: PaymentPausedPlanCardProps) {
  const { openUpdateCard, isAvailable } = useUpdatePaymentMethod()
  return <PausedPlanCard {...props} onUpdateCard={openUpdateCard} isUpdateCardAvailable={isAvailable} />
}
